import * as Sentry from '@sentry/react';
import { REDACTED_MARKER, SENSITIVE_AUDIT_FIELDS } from '../dataProvider';

/**
 * Sentry client-side error tracking (D-hardening-sentry, Phase 9 final hardening).
 *
 * The admin MIRRORS the member-web Sentry posture: a global capture floor (one top-level
 * Sentry.ErrorBoundary in main.tsx, per-surface boundaries deferred), tracesSampleRate 0
 * initially (error capture on, performance tracing off, revisit at launch), and the DSN read
 * from env with the real production DSN deferred to deploy provisioning (the infra-seam
 * category). This module isolates the config from the app shell so main.tsx stays thin and
 * the wiring is unit-testable.
 *
 * The DSN follows the cognito precedent: a public client identifier (safe in browser code, a
 * GitHub Actions Variable not a Secret), read with `?? ''` so an absent value is a clean
 * no-op and never hard-fails the build. Without a DSN (dev, CI) Sentry.init is never called.
 *
 * The admin-specific care is the scrubbing: the admin handles member PII, financial data, the
 * audit log, and write-only credentials, and NONE of that may reach Sentry. The redaction
 * discipline the build already holds (SENSITIVE_AUDIT_FIELDS + REDACTED_MARKER) EXTENDS to
 * error reporting, so beforeSend imports that same set as the one source of truth.
 */

// The error-reporting-specific PII keys, on top of the shared audit fields. Kept lower-case
// for a case-insensitive match (so a header 'Authorization' is caught alongside a body
// 'email'). Growing SENSITIVE_AUDIT_FIELDS in the audit floor grows this scrub set for free.
const PII_KEYS = ['email', 'token', 'authorization', 'password'];
const SCRUB_KEYS = new Set(
  [...SENSITIVE_AUDIT_FIELDS, ...PII_KEYS].map((key) => key.toLowerCase()),
);

/**
 * Deep-copy a value, redacting any object property whose key is in the scrub set. Arrays and
 * nested objects are walked; primitives pass through. A scrubbed field carries the same
 * REDACTED_MARKER the audit floor uses, so the intent is one and the same across both.
 */
const scrub = (input: unknown): unknown => {
  if (Array.isArray(input)) return input.map(scrub);
  if (input && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input)) {
      out[key] = SCRUB_KEYS.has(key.toLowerCase())
        ? REDACTED_MARKER
        : scrub(value);
    }
    return out;
  }
  return input;
};

// The exact beforeSend signature the SDK expects, derived from init's own options so the hook
// stays type-safe against the installed version.
type BeforeSend = NonNullable<
  NonNullable<Parameters<typeof Sentry.init>[0]>['beforeSend']
>;

/**
 * Scrub sensitive data from an error event before it leaves the browser. Walks the
 * data-bearing containers (extra, contexts, request, and each breadcrumb's data) and redacts
 * the sensitive keys. sendDefaultPii is false as well (below), so this is the belt-and-braces
 * floor over anything a future breadcrumb or context might carry.
 */
export const beforeSend: BeforeSend = (event) => {
  if (event.extra) event.extra = scrub(event.extra) as typeof event.extra;
  if (event.contexts)
    event.contexts = scrub(event.contexts) as typeof event.contexts;
  if (event.request)
    event.request = scrub(event.request) as typeof event.request;
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) =>
      crumb.data
        ? { ...crumb, data: scrub(crumb.data) as typeof crumb.data }
        : crumb,
    ) as typeof event.breadcrumbs;
  }
  return event;
};

/**
 * Initialise Sentry, GATED on the DSN being present. Called once at app startup (main.tsx).
 * With no DSN (dev, CI) this is a clean no-op: Sentry.init is never called, so nothing errors
 * and no events are transmitted. The ErrorBoundary in main.tsx still renders its fallback
 * either way; it just has nowhere to report to.
 */
export const initSentry = (): void => {
  const dsn = import.meta.env.VITE_SENTRY_DSN ?? '';
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Error capture ON, performance tracing OFF until the launch traffic shape lets a sample
    // rate be chosen (the member-web posture). Revisit at launch.
    tracesSampleRate: 0,
    // Do not attach PII (IP, cookies, user-identifying request data). The beforeSend scrub is
    // the second line for anything that slips into extra / contexts / breadcrumbs.
    sendDefaultPii: false,
    beforeSend,
  });
};
